<script setup lang="ts">
/** components */
import CommonButton from '~/components/common/Button.vue'
import DownloadIcon from '~/assets/icons/icon-download.svg'

/** types */
import type { Filter } from '~/types/daily-indicator.type'
import type { DateRangeType } from '~/types/date-range.type'

/** utils */
import {
  objectToXlsxInGroupsServicePro,
  downloadBlob,
} from '~/src/utils/download-xlsx'
import { logError } from '~/utils/log-error'

const props = defineProps<{
  rawData: any
  category: string
  title: string
  initialDateRangeName: Filter
  dateRange: DateRangeType
}>()

const emit = defineEmits(['onDateRangeChange', 'onChartDownload'])
const commonButtonTextClasses =
  'w-8 md:font-semibold font-normal text-center text-xs md:text-sm inline-block'

const filterNames = {
  oneWeek: 'oneWeek' as Filter,
  oneMonth: 'oneMonth' as Filter,
  threeMonths: 'threeMonths' as Filter,
  yearToDate: 'yearToDate' as Filter,
}

const filters = computed(() => [
  {
    name: filterNames.oneWeek,
    label: '1W',
  },
  {
    name: filterNames.oneMonth,
    label: '1M',
  },
  {
    name: filterNames.threeMonths,
    label: '3M',
  },
  {
    name: filterNames.yearToDate,
    label: 'YTD',
  },
])

const activeFilter = computed<Filter>({
  get: () => props.initialDateRangeName,
  set: (value) => {
    emit('onDateRangeChange', value)
  },
})

const onFilterChange = (filter: Filter) => {
  if (isActive(filter)) return
  activeFilter.value = filter
}

const isActive = (filter: Filter) => activeFilter.value === filter

const handleDownloadAsXlsx = (
  rawData,
  category: string,
  startDate: string,
  endDate: string,
  title: string,
) => {
  try {
    const blob = objectToXlsxInGroupsServicePro(category, rawData)
    downloadBlob(blob, startDate, endDate, title)

    emit('onChartDownload', { startDate, endDate, title })
  } catch (_error) {
    logError(_error)
  }
}
</script>

<template>
  <div class="flex gap-x-2">
    <CommonButton
      v-for="filter in filters"
      :key="filter.name"
      variant="clean"
      class="commonButtonClasses h-8 px-2 lg:h-[42px] lg:px-2.5 xl:px-[15px]"
      :class="{
        '!outline-p-700 !bg-s-100': isActive(filter.name),
      }"
      @click="onFilterChange(filter.name)"
    >
      <span :class="commonButtonTextClasses">{{ filter.label }}</span>
    </CommonButton>
    <button
      class="flex h-8 lg:h-[42px] border-black bg-black md:bg-transparent md:border-[#c1c5cf] items-center gap-x-1 border-2 rounded md:px-2.5 w-[50px] max-full md:w-full justify-center"
      @click="
        handleDownloadAsXlsx(
          rawData,
          category,
          dateRange.from,
          dateRange.to,
          title,
        )
      "
    >
      <DownloadIcon
        class="size-[18px] flex-shrink-0 md:hidden xl:inline-block md:text-black text-white"
      />
      <span
        class="hidden text-xs md:font-semibold md:text-sm md:inline-block md:w-[74px]"
      >
        Download
      </span>
    </button>
  </div>
</template>
